import React from 'react';
import { Button, Spin } from 'antd';
import s from './s.module.less';
import MedicationItem from './MedicationItem';
import MedicationModal from './MedicationModal';
import MedicationContentModal from './MedicationContentModal';
import ConfirmModal from './ConfirmModal';
import {
    DataManager,
    TabDataManager,
    generateTempId,
} from '../../utils/dataUpdater';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { TMedication, TMedicationContent } from '../../type';
import MedicationContentItem from './MedicationContentItem';
import { ServerMeta } from 'types/common';

type Props = {
    dataManager: DataManager;
    condition?: ServerMeta;
    onDataChange?: () => void;
};

const MedicationMgmt = ({ dataManager, condition, onDataChange }: Props) => {
    const [loading] = React.useState(false);
    const [activeTab, setActiveTab] = React.useState('content');
    const [modalVisible, setModalVisible] = React.useState(false);
    const [contentModalVisible, setContentModalVisible] = React.useState(false);
    const [selectedMedication, setSelectedMedication] = React.useState<TMedication | undefined>(undefined);

    // 删除确认Modal状态
    const [deleteModalVisible, setDeleteModalVisible] = React.useState(false);
    const [medicationToDelete, setMedicationToDelete] = React.useState<TMedication | undefined>(undefined);
    const [deleteLoading, setDeleteLoading] = React.useState(false);

    // 强制刷新状态 - 用于在拖拽后触发重新渲染
    const [refreshTrigger, setRefreshTrigger] = React.useState(0);

    // 为medicationMgmt tab创建独立的数据管理器
    const [medicationTabManager] = React.useState<TabDataManager>(() => {
        return dataManager.createTabManager('medicationMgmt');
    });

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    );

    // 从tab数据管理器获取medicationMgmt数据 - refreshTrigger用于强制刷新
    const medicationMgmtData = React.useMemo(() => {
        return medicationTabManager.getTabData('medicationMgmt');
    }, [medicationTabManager, refreshTrigger]);

    const handleMedicationEdit = (medication: TMedication) => {
        setSelectedMedication(medication);
        setModalVisible(true);
    };

    const handleMedicationSave = async (data: TMedication) => {
        try {
            if (selectedMedication) {
                // 编辑现有药物，保留原始priority
                const medications = medicationMgmtData?.medications || [];
                const index = medications.findIndex((med) => med === selectedMedication);
                if (index !== -1) {
                    const updatedMedication = {
                        ...data,
                        priority: selectedMedication.priority, // 保留原始priority
                    };
                    medicationTabManager.updateArrayItem('medications', index, updatedMedication);
                }
            } else {
                // 添加新药物，自动设置priority和临时ID
                const medications = medicationMgmtData?.medications || [];
                const newMedication = {
                    ...data,
                    priority: medications.length + 1, // 设置为当前数组长度 + 1
                    _tempId: generateTempId(), // 生成临时ID
                };
                medicationTabManager.addArrayItem('medications', newMedication);
            }

            // 保存到服务器
            await dataManager.saveDataToServer(
                () => {
                    setModalVisible(false);
                    setSelectedMedication(undefined);
                    // 通知主页面数据已更新，触发预览刷新
                    onDataChange?.();
                },
                (error: any) => {
                    console.error('Failed to save medication:', error);
                },
            );
        } catch (error) {
            console.error('Error in handleMedicationSave:', error);
        }
    };

    const handleAddMedication = () => {
        setSelectedMedication(undefined);
        setModalVisible(true);
    };

    const handleContentEdit = () => {
        setContentModalVisible(true);
    };

    const handleContentSave = async (data: TMedicationContent) => {
        try {
            // 更新内容数据
            medicationTabManager.updateTabData('content', data);

            // 保存到服务器
            await dataManager.saveDataToServer(
                () => {
                    setContentModalVisible(false);
                },
                (error: any) => {
                    console.error('Failed to save content:', error);
                },
            );
        } catch (error) {
            console.error('Error in handleContentSave:', error);
        }
    };

    // 显示删除确认Modal
    const handleDeleteClick = (medication: TMedication) => {
        setMedicationToDelete(medication);
        setDeleteModalVisible(true);
    };

    // 确认删除
    const handleConfirmDelete = async () => {
        if (!medicationToDelete) return;

        setDeleteLoading(true);
        try {
            const medications = medicationMgmtData?.medications || [];
            const index = medications.findIndex((med) => med === medicationToDelete);
            if (index !== -1) {
                medicationTabManager.removeArrayItem('medications', index);
            }

            // 保存到服务器
            await dataManager.saveDataToServer(
                () => {
                    setDeleteModalVisible(false);
                    setMedicationToDelete(undefined);
                    // 通知主页面数据已更新，触发预览刷新
                    onDataChange?.();
                },
                (error: any) => {
                    console.error('Failed to delete medication:', error);
                },
            );
        } catch (error) {
            console.error('Error in handleConfirmDelete:', error);
        } finally {
            setDeleteLoading(false);
        }
    };

    // 取消删除
    const handleCancelDelete = () => {
        setDeleteModalVisible(false);
        setMedicationToDelete(undefined);
    };

    const onMedicationDragEnd = async ({ active, over }: any) => {
        if (active.id !== over?.id) {
            const medications = medicationMgmtData?.medications || [];

            // 使用_tempId进行查找，如果没有则回退到name
            const oldIndex = medications.findIndex((med) =>
                (med as any)._tempId === active.id || med.name === active.id,
            );
            const newIndex = medications.findIndex((med) =>
                (med as any)._tempId === over?.id || med.name === over?.id,
            );

            // console.log('Medication drag indices:', { oldIndex, newIndex });

            if (oldIndex !== -1 && newIndex !== -1) {
                // 更新优先级
                const updatedMedications = [...medications];
                const [movedItem] = updatedMedications.splice(oldIndex, 1);
                updatedMedications.splice(newIndex, 0, movedItem);

                // 更新所有药物的优先级（从1开始）
                updatedMedications.forEach((med, index) => {
                    med.priority = index + 1;
                });

                // 批量更新数据 - 对于medicationMgmt，medications是嵌套数组，需要特殊处理
                const currentMedicationData = medicationTabManager.getTabData('medicationMgmt');
                const updatedMedicationData = {
                    ...currentMedicationData,
                    medications: updatedMedications,
                };

                medicationTabManager.updateTabDataDirect(updatedMedicationData);

                // 验证更新后的数据
                // const verifyData = medicationTabManager.getTabData('medicationMgmt');

                // 强制触发组件重新渲染
                setRefreshTrigger((prev) => prev + 1);

                // 通知主页面数据已更新，触发预览刷新
                onDataChange?.();

                // 保存到服务器（不等待，避免阻塞UI更新）
                dataManager.saveDataToServer(
                    () => {
                        console.log('Medication order updated successfully');
                    },
                    (error: any) => {
                        console.error('Failed to update medication order:', error);
                    },
                );
            } else {
                console.error('Could not find medication indices:', { oldIndex, newIndex });
            }
        }
    };

    return (
        <div className={s.wrap}>
            <Spin spinning={loading}>
                <div className={s.tabContainer}>
                    <div className={s.header}>
                        <h3>Medications</h3>
                        <Button type="primary" onClick={handleAddMedication}>
                            Add Medication
                        </Button>
                    </div>
                    <div className={s.tabHeader}>
                        <div
                            className={`${s.tab} ${activeTab === 'content' ? s.active : ''}`}
                            onClick={() => setActiveTab('content')}
                        >
                            Content
                        </div>
                        <div
                            className={`${s.tab} ${activeTab === 'medications' ? s.active : ''}`}
                            onClick={() => setActiveTab('medications')}
                        >
                            Medications
                        </div>
                    </div>

                    {activeTab === 'content' && (
                        <div className={s.contentSection}>
                            <MedicationContentItem
                                content={medicationMgmtData?.content}
                                condition={condition}
                                onEdit={handleContentEdit}
                            />
                        </div>
                    )}

                    {activeTab === 'medications' && (
                        <div className={s.medicationsSection}>
                            <DndContext sensors={sensors} onDragEnd={onMedicationDragEnd}>
                                <SortableContext
                                    items={medicationMgmtData?.medications?.map((med) => (med as any)._tempId || med.name) || []}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <div className={s.medicationsList}>
                                        {medicationMgmtData?.medications?.map((medication, index) => (
                                            <MedicationItem
                                                index={index}
                                                key={(medication as any)._tempId || medication.name}
                                                med={medication}
                                                onEdit={() => handleMedicationEdit(medication)}
                                                onRemove={() => handleDeleteClick(medication)}
                                            />
                                        ))}
                                    </div>
                                </SortableContext>
                            </DndContext>
                        </div>
                    )}
                </div>
            </Spin>

            {/* Medication Modal */}
            <MedicationModal
                visible={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    setSelectedMedication(undefined);
                }}
                onSave={handleMedicationSave}
                initialData={selectedMedication}
            />

            {/* Content Modal */}
            <MedicationContentModal
                visible={contentModalVisible}
                onCancel={() => {
                    setContentModalVisible(false);
                }}
                onSave={handleContentSave}
                initialData={medicationMgmtData?.content}
            />

            {/* 删除确认Modal */}
            <ConfirmModal
                visible={deleteModalVisible}
                title="Confirm Delete Medication"
                content={`Are you sure you want to delete medication "${medicationToDelete?.name}"? This action cannot be undone.`}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                confirmText="Delete"
                cancelText="Cancel"
                loading={deleteLoading}
            />
        </div>
    );
};

export default MedicationMgmt;
